// Fetches the canonical persisted transcript for the active session and
// mirrors it into the Zustand store via `hydrateMessages`. Legacy uses
// the same pattern (RTK Query + a setLocalMessages mirror inside
// ChatPanel) — V1 owns the same shape with TanStack Query.
//
// No polling. The fetcher runs:
//   • Once on session open (TanStack auto-fires on mount).
//   • Again whenever `useAgentForgeStream` invalidates the messages
//     query on `stream_done` / `agent_message` / `stream_error`.
//
// Mount this exactly once (in the `AgentForge` umbrella) so every
// surface reads the same Zustand mirror.

import { useEffect } from 'react';
import { toStoreInputs, useMessagesQuery } from '../api/messages';
import { useAgentForgeStore } from '../stores';

export const useAgentForgeMessages = (
  sessionId: string | null | undefined
): void => {
  const hydrateMessages = useAgentForgeStore((s) => s.hydrateMessages);
  const setTabStatus = useAgentForgeStore((s) => s.setTabStatus);
  const clearStreamingDraft = useAgentForgeStore((s) => s.clearStreamingDraft);
  const tabStatus = useAgentForgeStore(
    (s) => s.openTabs.find((t) => t.sessionId === sessionId)?.status
  );

  const query = useMessagesQuery({
    sessionId,
    enabled: !!sessionId,
    // No polling — legacy doesn't poll either. `useAgentForgeStream`
    // invalidates this query on `stream_done`, which triggers TanStack
    // to refetch.
    refetchIntervalMs: undefined,
  });

  useEffect(() => {
    const data = query.data;
    if (!sessionId || !data || data.length === 0) return;

    // Never hydrate mid-stream. The server transcript at this point is a
    // partial snapshot — usually [user, empty_assistant_tool_calls, tool]
    // with the final assistant prose still being generated. Folding tool
    // blocks into a missing-yet-final assistant turn drops them on the
    // floor, and `clearStreamingDraft()` below would then wipe the live
    // WS draft that *did* have the card. Wait for stream_done.
    const state = useAgentForgeStore.getState();
    const streaming =
      state.streamingDraft?.sessionId === sessionId ||
      state.openTabs.find((t) => t.sessionId === sessionId)?.status ===
        'streaming';
    if (streaming) return;

    // Don't clobber a fresh optimistic user message before the server
    // has caught up. Two gates:
    //   1. Server must have a new assistant turn we don't know about — no
    //      point replacing local with the same canonical state.
    //   2. Server must have at least as many user messages as we do —
    //      otherwise our trailing optimistic user (just typed, not yet
    //      mirrored back in the GET) would be wiped when we replace.
    const local = state.messagesBySession[sessionId] ?? [];
    const hasOptimisticUser = local.some((m) => m.role === 'user');
    if (hasOptimisticUser) {
      const knownAssistantIds = new Set(
        local.filter((m) => m.role === 'assistant').map((m) => m.id)
      );
      const serverHasNewAssistant = data.some(
        (m) => m.role === 'assistant' && !knownAssistantIds.has(m.id)
      );
      if (!serverHasNewAssistant) return;
      const localUserCount = local.filter((m) => m.role === 'user').length;
      const serverUserCount = data.filter((m) => m.role === 'user').length;
      if (serverUserCount < localUserCount) return;
    }

    hydrateMessages(sessionId, toStoreInputs(data));

    // Drop the WS draft in the same tick as the canonical record lands so
    // the streamed text swaps directly into the persisted message — no
    // "Thinking…" gap in between. `useAgentForgeStream` deliberately
    // leaves the draft alive on `stream_done` for this reason.
    clearStreamingDraft();

    // Flip the tab's status dot off once the canonical record has
    // landed. No-op for inline `/agent` (no tab in the open list).
    if (tabStatus === 'streaming') setTabStatus(sessionId, 'done');
  }, [
    sessionId,
    query.data,
    hydrateMessages,
    clearStreamingDraft,
    setTabStatus,
    tabStatus,
  ]);
};
